// Source-first дерево импорта workspace (зеркалит export-tree, плюс статусы и «переезд»).
// Загружает analyze-план (graph + flat-arrays). Рендерит:
//   1) Workspace-блок (settings/LLM/theme) сверху
//   2) Folders, Lists — блоки с чекбоксами
//   3) Sources → Templates → Components (основное дерево)
//   4) Unlinked — компоненты без source/template + templates без компонентов
//   5) Pages-шорткат в левой панели
// Синхронизирует выбор в hidden inputs selection[*][] перед submit формы импорта.

import { Controller } from "@hotwired/stimulus"

const STATUS_COLORS = {
  new:       { bg: "#DCFCE7", fg: "#166534" },
  update:    { bg: "#FEF3C7", fg: "#92400E" },
  unchanged: { bg: "#F3F4F6", fg: "#6B7280" }
}

export default class extends Controller {
  static targets = [
    "loadBtn", "indicator", "indicatorText",
    "emptyState", "groupsPane", "groupsList", "detailPane",
    "selectionBar", "selectionCount", "selectionBreakdown"
  ]
  static values = { analyzeUrl: String }

  connect() {
    this.plan = null
    this.expanded = new Set()
    this.selected = {
      sources: new Set(),
      item_templates: new Set(),
      components: new Set(),
      pages: new Set(),
      folders: new Set(),
      lists: new Set(),
      workspace: new Set()
    }
    this.query = ""

    const form = this._form || this.element.querySelector("form") || this.element.querySelector("form")
    form?.addEventListener("submit", this._injectHiddenInputs.bind(this))
    this._form = form
  }

  // ───────────────────────────────────────────── LOAD

  async analyze(event) {
    event.preventDefault()
    const form = this._form || this.element.querySelector("form")
    if (!form) return

    if (this.hasLoadBtnTarget) {
      this.loadBtnTarget.disabled = true
      this._loadBtnOriginal = this.loadBtnTarget.innerHTML
      this.loadBtnTarget.innerHTML = "Анализ..."
    }
    try {
      const fd = new FormData(form)
      const body = new URLSearchParams()
      for (const [k, v] of fd.entries()) body.append(k, v)

      const res = await fetch(this.analyzeUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this._csrfToken() },
        body
      })
      const data = await res.json()
      if (!data.success) {
        alert((data.errors || ["Ошибка анализа"]).join(", "))
        return
      }
      this.plan = data.plan
      this._seedDefaults()
      this._render()
      this._showIndicator()
    } catch (e) {
      alert(`Ошибка: ${e.message}`)
    } finally {
      if (this.hasLoadBtnTarget) {
        this.loadBtnTarget.disabled = false
        this.loadBtnTarget.innerHTML = this._loadBtnOriginal
      }
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _seedDefaults() {
    // По умолчанию — всё отмечено. Unchanged тоже (чтобы user явно снял, если не нужно).
    this.selected.sources = new Set((this.plan.sources || []).map(s => s.code))
    this.selected.item_templates = new Set((this.plan.item_templates || []).map(t => t.code))
    this.selected.components = new Set((this.plan.components || []).map(c => c.code))
    this.selected.pages = new Set((this.plan.pages || []).map(p => p.code))
    this.selected.folders = new Set((this.plan.folders || []).map(f => f.code))
    this.selected.lists = new Set((this.plan.lists || []).map(l => l.code))
    this.selected.workspace = new Set(["workspace"])
    if (this.plan.workspace_llm_settings_present) this.selected.workspace.add("workspace_llm_settings")
    if (this.plan.workspace_settings_present) this.selected.workspace.add("workspace_settings")

    // Раскроем все source-узлы и unlinked по-умолчанию
    this.expanded = new Set([
      ...(this.plan.sources || []).map(s => `source:${s.code}`),
      "section:unlinked"
    ])
  }

  _showIndicator() {
    if (!this.hasIndicatorTarget) return
    const countBy = (arr, status) => (arr || []).filter(x => x.status === status).length
    const nNew = countBy(this.plan.components, "new") + countBy(this.plan.sources, "new") + countBy(this.plan.item_templates, "new") + countBy(this.plan.pages, "new")
    const nUpd = countBy(this.plan.components, "update") + countBy(this.plan.sources, "update") + countBy(this.plan.item_templates, "update") + countBy(this.plan.pages, "update")
    const nUnc = countBy(this.plan.components, "unchanged") + countBy(this.plan.sources, "unchanged") + countBy(this.plan.item_templates, "unchanged") + countBy(this.plan.pages, "unchanged")
    this.indicatorTextTarget.innerHTML = `
      <span>NEW <b>${nNew}</b></span>
      <span>·</span>
      <span>UPDATE <b>${nUpd}</b></span>
      <span>·</span>
      <span>unchanged <b>${nUnc}</b></span>
    `
    this.indicatorTarget.classList.remove("hidden")
    this.indicatorTarget.classList.add("inline-flex")
  }

  // ───────────────────────────────────────────── RENDER

  _render() {
    this.emptyStateTarget.classList.add("hidden")
    this.groupsPaneTarget.classList.remove("hidden")
    this.groupsPaneTarget.classList.add("flex", "flex-col")
    this.detailPaneTarget.classList.remove("hidden")
    this.detailPaneTarget.classList.add("flex")

    this._renderGroupsPane()
    this._renderDetail()
    this._updateSelectionBar()
  }

  _renderGroupsPane() {
    const P = this.plan
    const counters = (arr, key) => `${this.selected[key].size}/${(arr || []).length}`

    let html = `
      <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Итог</div>
      <div class="mb-3 rounded-[10px] border border-[#E6E8EC] bg-white p-2.5 text-[12px] leading-5">
        <div class="flex justify-between"><span class="text-[#6B7280]">Источники</span><span class="font-mono">${counters(P.sources, "sources")}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Шаблоны</span><span class="font-mono">${counters(P.item_templates, "item_templates")}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Компоненты</span><span class="font-mono">${counters(P.components, "components")}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Папки</span><span class="font-mono">${counters(P.folders, "folders")}</span></div>
        <div class="flex justify-between"><span class="text-[#6B7280]">Списки</span><span class="font-mono">${counters(P.lists, "lists")}</span></div>
      </div>

      <div class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Страницы (шорткат)</div>
      <div class="flex flex-col gap-0.5">
    `
    for (const page of P.pages || []) {
      const state = this._pageState(page)
      const icon = state === "checked" ? "☑" : (state === "indeterminate" ? "◪" : "☐")
      const selCount = (page.component_codes || []).filter(c => this.selected.components.has(c)).length
      html += `
        <button type="button"
                data-action="click->dae20--workspace-import-tree#togglePage"
                data-page-code="${this._esc(page.code)}"
                class="flex items-center gap-2 w-full rounded-[6px] px-2 py-1.5 text-[12.5px] text-left hover:bg-white/70">
          <span class="w-4 text-center">${icon}</span>
          <span class="flex-1 truncate">${this._esc(page.name || page.code)}</span>
          ${this._statusBadge(page.status)}
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${selCount}/${(page.component_codes || []).length}</span>
        </button>
      `
    }
    html += `</div>`
    this.groupsListTarget.innerHTML = html
  }

  _renderDetail() {
    let html = `
      <div class="sticky top-0 z-10 bg-white border-b border-[#E6E8EC] px-4 py-2.5">
        <input type="search"
               value="${this._esc(this.query)}"
               placeholder="Поиск по коду или имени…"
               data-action="input->dae20--workspace-import-tree#onSearch"
               class="h-[30px] w-full px-3 border border-[#E6E8EC] rounded-[7px] bg-[#FBFBFD] text-[12.5px] outline-none"/>
      </div>
      <div class="flex-1 overflow-y-auto px-3 py-3">
    `

    html += this._renderWorkspaceBlock()
    html += this._renderFoldersBlock()
    html += this._renderListsBlock()

    // Main tree: sources
    const sources = this._filteredSources()
    html += `<div class="mt-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Источники и зависимости</div>`
    if (sources.length === 0) {
      html += `<div class="p-4 text-center text-[#9AA1AC] text-[12.5px]">Нет источников в payload</div>`
    } else {
      for (const src of sources) html += this._renderSource(src)
    }

    // Unlinked section
    html += this._renderUnlinkedBlock()

    html += `</div>`
    this.detailPaneTarget.innerHTML = html
  }

  _renderWorkspaceBlock() {
    const ws = this.plan.workspace || {}
    const has = (key) => this.selected.workspace.has(key)
    const toggleAttr = (key) => `data-action="change->dae20--workspace-import-tree#toggleWorkspaceKey" data-workspace-key="${key}"`
    return `
      <div class="mb-3 rounded-[8px] border border-[#E6E8EC] bg-white p-3">
        <div class="flex items-center gap-2 mb-2">
          <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#EDE9FE] text-[#6D28D9] text-[10px] font-bold uppercase tracking-wide">workspace</span>
          <span class="text-[13px] font-semibold">${this._esc(ws.name || ws.code || "")}</span>
          <span class="font-mono text-[11px] text-[#9AA1AC]">${this._esc(ws.code || "")}</span>
          ${this._statusBadge(ws.status)}
        </div>
        <div class="grid grid-cols-3 gap-2 text-[12px]">
          <label class="flex items-center gap-2">
            <input type="checkbox" ${has("workspace") ? "checked" : ""} ${toggleAttr("workspace")} class="cursor-pointer" style="width:14px;height:14px;accent-color:#3D5AFE;">
            <span>Основная запись</span>
          </label>
          <label class="flex items-center gap-2 ${this.plan.workspace_llm_settings_present ? "" : "opacity-40"}">
            <input type="checkbox" ${has("workspace_llm_settings") ? "checked" : ""} ${toggleAttr("workspace_llm_settings")} class="cursor-pointer" style="width:14px;height:14px;accent-color:#3D5AFE;" ${this.plan.workspace_llm_settings_present ? "" : "disabled"}>
            <span>LLM settings${this.plan.workspace_llm_settings_present ? "" : " (нет в payload)"}</span>
          </label>
          <label class="flex items-center gap-2 ${this.plan.workspace_settings_present ? "" : "opacity-40"}">
            <input type="checkbox" ${has("workspace_settings") ? "checked" : ""} ${toggleAttr("workspace_settings")} class="cursor-pointer" style="width:14px;height:14px;accent-color:#3D5AFE;" ${this.plan.workspace_settings_present ? "" : "disabled"}>
            <span>Settings (theme/welcome/widgets)${this.plan.workspace_settings_present ? "" : " (нет в payload)"}</span>
          </label>
        </div>
      </div>
    `
  }

  _renderFoldersBlock() {
    const folders = this.plan.folders || []
    if (folders.length === 0) return ""
    const selected = folders.filter(f => this.selected.folders.has(f.code)).length

    const rows = folders.map(f => `
      <label class="flex items-center gap-2 py-1">
        <input type="checkbox" ${this.selected.folders.has(f.code) ? "checked" : ""}
               data-action="change->dae20--workspace-import-tree#toggleSimpleItem"
               data-group="folders" data-code="${this._esc(f.code)}"
               class="cursor-pointer" style="width:13px;height:13px;accent-color:#3D5AFE;">
        <span class="text-[11.5px] font-semibold">${this._esc(f.name || f.code)}</span>
        <span class="font-mono text-[10.5px] text-[#9AA1AC]">${this._esc(f.code)}</span>
        <span class="text-[10px] text-[#9AA1AC]">${this._esc(f.kind || "")}</span>
        ${this._statusBadge(f.status)}
      </label>
    `).join("")

    return `
      <div class="mb-3 rounded-[8px] border border-[#E6E8EC] bg-white p-3">
        <div class="flex items-center justify-between mb-1.5">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Папки</div>
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${selected}/${folders.length}</span>
        </div>
        ${rows}
      </div>
    `
  }

  _renderListsBlock() {
    const lists = this.plan.lists || []
    if (lists.length === 0) return ""
    const selected = lists.filter(l => this.selected.lists.has(l.code)).length

    const rows = lists.map(l => `
      <label class="flex items-center gap-2 py-1">
        <input type="checkbox" ${this.selected.lists.has(l.code) ? "checked" : ""}
               data-action="change->dae20--workspace-import-tree#toggleSimpleItem"
               data-group="lists" data-code="${this._esc(l.code)}"
               class="cursor-pointer" style="width:13px;height:13px;accent-color:#3D5AFE;">
        <span class="text-[11.5px] font-semibold">${this._esc(l.name || l.code)}</span>
        <span class="font-mono text-[10.5px] text-[#9AA1AC]">${this._esc(l.code)}</span>
        ${this._statusBadge(l.status)}
      </label>
    `).join("")

    return `
      <div class="mb-3 rounded-[8px] border border-[#E6E8EC] bg-white p-3">
        <div class="flex items-center justify-between mb-1.5">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">Списки</div>
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${selected}/${lists.length}</span>
        </div>
        ${rows}
      </div>
    `
  }

  _renderSource(src) {
    const id = `source:${src.code}`
    const state = this._sourceState(src)
    const isExp = this.expanded.has(id)
    const templates = (src.used_by_templates || []).map(c => this.plan.item_templates.find(t => t.code === c)).filter(Boolean)
    const directComps = (src.used_by_components_direct || []).map(c => this.plan.components.find(x => x.code === c)).filter(Boolean)
    const totalChildren = templates.length + directComps.length

    let html = `
      <div class="mb-1 rounded-[8px] border border-[#E6E8EC] bg-white">
        <div class="flex items-center gap-2 px-3 py-2">
          <button type="button"
                  data-action="click->dae20--workspace-import-tree#toggleExpand"
                  data-node-id="${id}"
                  class="w-4 text-[12px] text-[#6B7280] ${totalChildren === 0 ? 'invisible' : ''}">${isExp ? "▾" : "▸"}</button>
          ${this._checkbox(state, `toggleSource`, { sourceCode: src.code })}
          <span class="flex items-center gap-2 flex-1 min-w-0">
            <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#EEF2FF] text-[#3D5AFE] text-[10px] font-bold uppercase tracking-wide">source</span>
            <span class="text-[13px] font-semibold truncate">${this._esc(src.name || src.code)}</span>
            <span class="font-mono text-[11px] text-[#9AA1AC] truncate">${this._esc(src.code)}</span>
            ${this._statusBadge(src.status)}
            ${this._changedFieldsPill(src.changed_fields)}
          </span>
          <span class="font-mono text-[10.5px] text-[#9AA1AC]">${totalChildren}</span>
        </div>
    `

    if (isExp && totalChildren > 0) {
      html += `<div class="border-t border-[#F0F1F4] pl-7 pr-2 py-1.5 bg-[#FBFBFD]">`
      for (const tpl of templates) html += this._renderTemplate(tpl)
      for (const c of directComps) html += this._renderComponent(c, { direct: true })
      html += `</div>`
    }
    html += `</div>`
    return html
  }

  _renderTemplate(tpl) {
    const id = `template:${tpl.code}`
    const state = this._templateState(tpl)
    const isExp = this.expanded.has(id)
    const comps = (tpl.used_by_components || []).map(c => this.plan.components.find(x => x.code === c)).filter(Boolean)

    let html = `
      <div class="mb-0.5">
        <div class="flex items-center gap-2 py-1">
          <button type="button"
                  data-action="click->dae20--workspace-import-tree#toggleExpand"
                  data-node-id="${id}"
                  class="w-4 text-[12px] text-[#6B7280] ${comps.length === 0 ? 'invisible' : ''}">${isExp ? "▾" : "▸"}</button>
          ${this._checkbox(state, `toggleTemplate`, { templateCode: tpl.code })}
          <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#FEF3C7] text-[#B45309] text-[10px] font-bold uppercase tracking-wide">template</span>
          <span class="text-[12.5px] font-semibold truncate">${this._esc(tpl.name || tpl.code)}</span>
          <span class="font-mono text-[11px] text-[#9AA1AC] truncate">${this._esc(tpl.code)}</span>
          ${this._statusBadge(tpl.status)}
          ${this._changedFieldsPill(tpl.changed_fields)}
          <span class="font-mono text-[10.5px] text-[#9AA1AC] ml-auto">${comps.length}</span>
        </div>
    `

    if (isExp && comps.length > 0) {
      html += `<div class="pl-6">`
      for (const c of comps) html += this._renderComponent(c, { direct: false })
      html += `</div>`
    }
    html += `</div>`
    return html
  }

  _renderComponent(c, { direct }) {
    const checked = this.selected.components.has(c.code)
    const state = checked ? "checked" : "unchecked"
    const moves = (c.status === "update" && (c.moves_workspace || c.moves_page))
    const moveHTML = moves ? `
      <span class="inline-flex items-center px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B] text-[9.5px] font-bold uppercase tracking-wide"
            title="Переезд: ws=${this._esc(c.current_workspace_code || "?")} → текущий; page=${this._esc(c.current_page_code || "?")} → ${this._esc(c.page_code || "?")}">переезд</span>` : ""

    return `
      <div class="flex items-center gap-2 py-0.5">
        <span class="w-4"></span>
        ${this._checkbox(state, `toggleComponent`, { componentCode: c.code })}
        <span class="inline-flex items-center px-2 h-[18px] rounded bg-[#ECFCCB] text-[#4D7C0F] text-[10px] font-bold uppercase tracking-wide">component</span>
        <span class="text-[12px] font-semibold truncate">${this._esc(c.name || c.code)}</span>
        <span class="font-mono text-[10.5px] text-[#9AA1AC] truncate">${this._esc(c.code)}</span>
        ${c.template_code ? `<span class="inline-flex items-center px-1.5 h-[16px] rounded bg-[#F3F4F6] text-[#6B7280] font-mono text-[10px]">${this._esc(c.template_code)}</span>` : ""}
        ${direct ? `<span class="text-[9.5px] text-[#6B7280] bg-[#F3F4F6] rounded px-1.5 py-0.5">direct</span>` : ""}
        ${this._statusBadge(c.status)}
        ${this._changedFieldsPill(c.changed_fields)}
        ${moveHTML}
        <span class="ml-auto text-[10.5px] text-[#9AA1AC]">page: <span class="font-mono">${this._esc(c.page_code || "—")}</span></span>
      </div>
    `
  }

  _renderUnlinkedBlock() {
    const g = this.plan.graph || {}
    const uCompCodes = g.unlinked_component_codes || []
    const uTplCodes = g.unlinked_template_codes || []
    if (uCompCodes.length === 0 && uTplCodes.length === 0) return ""

    const id = "section:unlinked"
    const isExp = this.expanded.has(id)
    const uComps = uCompCodes.map(c => this.plan.components.find(x => x.code === c)).filter(Boolean)
    const uTpls  = uTplCodes.map(c => this.plan.item_templates.find(t => t.code === c)).filter(Boolean)

    let html = `
      <div class="mt-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9AA1AC]">
        Без привязки к источнику <span class="font-mono text-[#9AA1AC]">(${uComps.length + uTpls.length})</span>
      </div>
      <div class="mb-1 rounded-[8px] border border-[#E6E8EC] bg-white">
        <div class="flex items-center gap-2 px-3 py-2">
          <button type="button"
                  data-action="click->dae20--workspace-import-tree#toggleExpand"
                  data-node-id="${id}"
                  class="w-4 text-[12px] text-[#6B7280]">${isExp ? "▾" : "▸"}</button>
          <span class="flex-1 text-[12.5px] text-[#6B7280]">Single-компоненты и пустые шаблоны</span>
        </div>
    `
    if (isExp) {
      html += `<div class="border-t border-[#F0F1F4] pl-7 pr-2 py-1.5 bg-[#FBFBFD]">`
      for (const tpl of uTpls) html += this._renderTemplate(tpl)
      for (const c of uComps) html += this._renderComponent(c, { direct: false })
      html += `</div>`
    }
    html += `</div>`
    return html
  }

  // ───────────────────────────────────────────── HELPERS

  _statusBadge(status) {
    if (!status) return ""
    const c = STATUS_COLORS[status] || STATUS_COLORS.unchanged
    return `<span class="inline-flex items-center px-1.5 h-[16px] rounded text-[9.5px] font-bold uppercase tracking-wide" style="background:${c.bg};color:${c.fg};">${status}</span>`
  }

  _changedFieldsPill(fields) {
    if (!Array.isArray(fields) || fields.length === 0) return ""
    return `<span class="font-mono text-[10px] text-[#8A5A00] bg-[#FEF3C7]/50 rounded px-1.5 py-0.5 truncate max-w-[240px]" title="${this._esc(fields.join(", "))}">${this._esc(fields.slice(0, 3).join(", "))}${fields.length > 3 ? "…" : ""}</span>`
  }

  _checkbox(state, action, dataset) {
    const dataAttrs = Object.entries(dataset).map(([k, v]) => `data-${this._kebab(k)}="${this._esc(v)}"`).join(" ")
    const indeterminate = state === "indeterminate"
    const checked = state === "checked"
    return `
      <input type="checkbox"
             ${checked ? "checked" : ""}
             ${indeterminate ? 'data-indeterminate="1"' : ""}
             ${dataAttrs}
             data-action="change->dae20--workspace-import-tree#${action}"
             class="w-[14px] h-[14px] cursor-pointer"
             style="accent-color:#3D5AFE;"/>
    `
  }

  _kebab(s) { return s.replace(/([A-Z])/g, "-$1").toLowerCase() }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  _applyIndeterminates() {
    this.detailPaneTarget.querySelectorAll('input[type=checkbox][data-indeterminate="1"]').forEach(el => { el.indeterminate = true })
  }

  _filteredSources() {
    const q = this.query.trim().toLowerCase()
    const all = this.plan.sources || []
    if (!q) return all
    const match = s => (s.code + " " + (s.name || "")).toLowerCase().includes(q)
    return all.filter(src => {
      if (match(src)) return true
      const tpls = (src.used_by_templates || []).map(c => this.plan.item_templates.find(t => t.code === c)).filter(Boolean)
      if (tpls.some(t => match(t))) return true
      const cmps = [
        ...(src.used_by_components_direct || []).map(c => this.plan.components.find(x => x.code === c)).filter(Boolean),
        ...tpls.flatMap(t => (t.used_by_components || []).map(c => this.plan.components.find(x => x.code === c)).filter(Boolean))
      ]
      return cmps.some(c => match(c))
    })
  }

  // ───────────────────────────────────────────── STATE

  _sourceState(src) {
    const tplCodes = src.used_by_templates || []
    const directCmpCodes = src.used_by_components_direct || []
    const allTplsChecked = tplCodes.every(c => this.selected.item_templates.has(c))
    const allDirectCmpsChecked = directCmpCodes.every(c => this.selected.components.has(c))
    const selfChecked = this.selected.sources.has(src.code)
    const anyChildChecked =
      tplCodes.some(c => this.selected.item_templates.has(c)) ||
      directCmpCodes.some(c => this.selected.components.has(c))
    if (selfChecked && allTplsChecked && allDirectCmpsChecked) return "checked"
    if (selfChecked || anyChildChecked) return "indeterminate"
    return "unchecked"
  }

  _templateState(tpl) {
    const cmps = tpl.used_by_components || []
    const allChecked = cmps.every(c => this.selected.components.has(c))
    const anyChecked = cmps.some(c => this.selected.components.has(c))
    const self = this.selected.item_templates.has(tpl.code)
    if (self && allChecked) return "checked"
    if (self || anyChecked) return "indeterminate"
    return "unchecked"
  }

  _pageState(page) {
    const cmps = page.component_codes || []
    if (cmps.length === 0) return this.selected.pages.has(page.code) ? "checked" : "unchecked"
    const checked = cmps.filter(c => this.selected.components.has(c)).length
    if (checked === 0) return "unchecked"
    if (checked === cmps.length && this.selected.pages.has(page.code)) return "checked"
    return "indeterminate"
  }

  // ───────────────────────────────────────────── ACTIONS

  onSearch(e) {
    this.query = e.target.value || ""
    this._renderDetail()
    this._applyIndeterminates()
  }

  toggleExpand(e) {
    const id = e.currentTarget.dataset.nodeId
    if (this.expanded.has(id)) this.expanded.delete(id); else this.expanded.add(id)
    this._renderDetail()
    this._applyIndeterminates()
  }

  toggleSource(e) {
    const code = e.currentTarget.dataset.sourceCode
    const src = this.plan.sources.find(s => s.code === code)
    if (!src) return
    this._setSource(src, e.currentTarget.checked)
    this._rerender()
  }

  _setSource(src, on) {
    if (on) this.selected.sources.add(src.code); else this.selected.sources.delete(src.code)
    for (const tc of src.used_by_templates || []) {
      const tpl = this.plan.item_templates.find(t => t.code === tc)
      if (tpl) this._setTemplate(tpl, on)
    }
    for (const cc of src.used_by_components_direct || []) this._setComponent(cc, on)
  }

  toggleTemplate(e) {
    const code = e.currentTarget.dataset.templateCode
    const tpl = this.plan.item_templates.find(t => t.code === code)
    if (!tpl) return
    this._setTemplate(tpl, e.currentTarget.checked)
    this._rerender()
  }

  _setTemplate(tpl, on) {
    if (on) this.selected.item_templates.add(tpl.code); else this.selected.item_templates.delete(tpl.code)
    for (const cc of tpl.used_by_components || []) this._setComponent(cc, on)
  }

  toggleComponent(e) {
    this._setComponent(e.currentTarget.dataset.componentCode, e.currentTarget.checked)
    this._rerender()
  }

  _setComponent(code, on) {
    if (on) {
      this.selected.components.add(code)
      const comp = this.plan.components.find(c => c.code === code)
      if (comp?.page_code) this.selected.pages.add(comp.page_code)
    } else {
      this.selected.components.delete(code)
    }
  }

  togglePage(e) {
    const code = e.currentTarget.dataset.pageCode
    const page = this.plan.pages.find(p => p.code === code)
    if (!page) return
    const state = this._pageState(page)
    const turnOn = state !== "checked"
    if (turnOn) this.selected.pages.add(page.code); else this.selected.pages.delete(page.code)
    for (const cc of page.component_codes || []) this._setComponent(cc, turnOn)
    this._rerender()
  }

  toggleWorkspaceKey(e) {
    const key = e.currentTarget.dataset.workspaceKey
    if (e.currentTarget.checked) this.selected.workspace.add(key); else this.selected.workspace.delete(key)
    this._updateSelectionBar()
  }

  toggleSimpleItem(e) {
    const group = e.currentTarget.dataset.group
    const code = e.currentTarget.dataset.code
    if (!this.selected[group]) return
    if (e.currentTarget.checked) this.selected[group].add(code); else this.selected[group].delete(code)
    this._renderGroupsPane()
    this._renderDetail()
    this._applyIndeterminates()
    this._updateSelectionBar()
  }

  _rerender() {
    this._renderGroupsPane()
    this._renderDetail()
    this._applyIndeterminates()
    this._updateSelectionBar()
  }

  _updateSelectionBar() {
    if (!this.hasSelectionBarTarget) return
    const total = this.selected.sources.size + this.selected.item_templates.size +
                  this.selected.components.size + this.selected.pages.size +
                  this.selected.folders.size + this.selected.lists.size +
                  this.selected.workspace.size
    this.selectionBarTarget.classList.remove("hidden")
    this.selectionBarTarget.classList.add("flex")
    this.selectionCountTarget.textContent = total
    this.selectionBreakdownTarget.innerHTML = `
      <span>${this.selected.sources.size} sources</span>·
      <span>${this.selected.item_templates.size} templates</span>·
      <span>${this.selected.components.size} components</span>·
      <span>${this.selected.pages.size} pages</span>·
      <span>${this.selected.folders.size} folders</span>·
      <span>${this.selected.lists.size} lists</span>
    `
  }

  // ───────────────────────────────────────────── SUBMIT

  _injectHiddenInputs(_event) {
    if (!this.plan) return
    const form = this._form || this.element.querySelector("form")
    if (!form) return

    form.querySelectorAll('input[data-tree-selection="1"]').forEach(n => n.remove())

    const add = (name, value) => {
      const inp = document.createElement("input")
      inp.type = "hidden"
      inp.name = name
      inp.value = value
      inp.dataset.treeSelection = "1"
      form.appendChild(inp)
    }

    // Per-item groups
    const pairs = [
      ["sources", this.selected.sources],
      ["item_templates", this.selected.item_templates],
      ["components", this.selected.components],
      ["pages", this.selected.pages],
      ["folders", this.selected.folders],
      ["lists", this.selected.lists]
    ]
    for (const [key, set] of pairs) {
      add(`selection[${key}][]`, "")
      for (const v of set) add(`selection[${key}][]`, v)
    }

    // Workspace booleans
    add(`selection[workspace]`, this.selected.workspace.has("workspace") ? "true" : "false")
    add(`selection[workspace_llm_settings]`, this.selected.workspace.has("workspace_llm_settings") ? "true" : "false")
    add(`selection[workspace_settings]`, this.selected.workspace.has("workspace_settings") ? "true" : "false")
  }
}
